/* =====================================================================
 * @auw/tenant — Workspace resolver helpers (Phase 4c).
 *
 * Turn an opaque `TenantManifest` into concrete values the shell can
 * consume:
 *   - `resolveTenantModeOrder`: the sidebar "Workspace" section ordering.
 *   - `resolveTenantDefaultMode`: the preferred post-login mode id.
 *   - `resolveTenantDefaultPath`: the URL path used for post-login
 *     redirects and the `/` root fallback.
 *
 * Design note
 *   `@auw/tenant` must not depend on `@auw/app-shell` (presentation)
 *   because the tenant package is itself a peer of app-shell. To keep
 *   the mode-id validation in the right place, callers pass a
 *   `WorkspaceModeGuard` predicate; `@auw/app-shell` exports
 *   `isWorkspaceMode` which satisfies that contract exactly.
 * ===================================================================== */

import type { WorkspaceMode } from '@auw/types';
import type { TenantManifest } from './types';

/**
 * Runtime check: returns `true` when the input is a known workspace
 * mode id. Supplied by `@auw/app-shell` (`isWorkspaceMode`).
 */
export type WorkspaceModeGuard = (value: string) => value is WorkspaceMode;

/**
 * Resolve the effective sidebar mode order.
 *
 * When the tenant manifest specifies an explicit order, unknown or
 * duplicate ids are silently dropped but the remaining ids preserve
 * their caller-specified ordering. When the override is absent /
 * empty, the `fallback` (typically `WORKSPACE_ORDER` from app-shell)
 * wins — B2C relies on that for exact ordering parity.
 *
 * The result is a fresh array (never the caller's `fallback`
 * reference) when an override fires, so consumers can safely cache
 * it without aliasing the app-shell default.
 */
export function resolveTenantModeOrder(
  tenant: TenantManifest,
  fallback: readonly WorkspaceMode[],
  isWorkspaceMode: WorkspaceModeGuard,
): readonly WorkspaceMode[] {
  const override = tenant.workspace?.modeOrder;
  if (!override || override.length === 0) return fallback;

  const seen = new Set<WorkspaceMode>();
  const result: WorkspaceMode[] = [];
  for (const raw of override) {
    if (typeof raw !== 'string') continue;
    if (!isWorkspaceMode(raw)) continue;
    if (seen.has(raw)) continue;
    seen.add(raw);
    result.push(raw);
  }
  return result.length > 0 ? result : fallback;
}

/**
 * Resolve the preferred post-login mode. Returns `undefined` when
 * the tenant did not specify one, or the specified id is not a known
 * mode — callers should fall back to `resolveTenantDefaultPath` in
 * that case (the router only understands paths).
 */
export function resolveTenantDefaultMode(
  tenant: TenantManifest,
  isWorkspaceMode: WorkspaceModeGuard,
): WorkspaceMode | undefined {
  const raw = tenant.workspace?.defaultMode;
  if (typeof raw !== 'string' || raw.length === 0) return undefined;
  return isWorkspaceMode(raw) ? raw : undefined;
}

/**
 * Resolve the literal redirect path used by `AppRouter`, `AuthGuard`,
 * `Layout`, and `AuthCallbackPage`. B2C keeps the legacy `/widgets`
 * landing by declaring it in the manifest; on-prem tenants can point
 * this wherever their deployment exposes the first screen.
 *
 * The default `fallback` value is `/widgets` to match the hard-coded
 * behaviour that existed before Phase 4c — callers in enterprise
 * builds can pass a different fallback if they ship a tenant without
 * a `workspace.defaultPath` field.
 */
export function resolveTenantDefaultPath(
  tenant: TenantManifest,
  fallback: string = '/widgets',
): string {
  const raw = tenant.workspace?.defaultPath;
  if (typeof raw !== 'string') return fallback;
  const trimmed = raw.trim();
  return trimmed.length > 0 ? trimmed : fallback;
}

/**
 * Resolve the path used by `InviteAuthedRedirect` — the route that
 * catches `/invite/team/:token` for already-authed users and bounces
 * them onto the tenant's normal workspace while preserving the team
 * token as a query param.
 *
 * Resolution order:
 *   1. `tenant.auth.inviteRedirectPath` — explicit tenant config.
 *   2. `tenant.workspace.defaultPath` — landing fallback so invite
 *      links still land on *some* sensible page when a tenant has
 *      not opted into a bespoke invite redirect (e.g. on-prem
 *      deployments that accept invites but have no `/agents` route).
 *   3. `'/widgets'` — final safety net matching the pre-Phase-11
 *      behaviour when the manifest omits both fields.
 *
 * The returned string is the **base path** without any query string;
 * callers append `?teamInvite=<token>` themselves so the encoding is
 * done at the call site rather than inside the tenant layer.
 */
export function resolveTenantInviteRedirectPath(
  tenant: TenantManifest,
): string {
  const configured = tenant.auth?.inviteRedirectPath;
  if (typeof configured === 'string') {
    const trimmed = configured.trim();
    if (trimmed.length > 0) return trimmed;
  }
  return resolveTenantDefaultPath(tenant);
}
